import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  LinearProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  TrendingUp,
  Speed,
  CheckCircle,
  Schedule,
  ShowChart,
  Analytics as AnalyticsIcon,
  EmojiEvents,
  People,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  sprintVelocityData,
  taskCompletionData,
  burndownData,
  productivityInsights,
} from './mockAnalyticsData';
import dashboardApi from '../../api/endpoints/dashboardApi';
import { DashboardSummary } from '../../api/types/DashboardTypes';
import { timeTrackingApi, TimeEntry } from '../../api/endpoints/timeTrackingApi';
import './Analytics.scss';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// A small "Sample data" chip so charts we can't back with a real endpoint
// yet (no historical burndown/velocity snapshots exist in the schema)
// read as illustrative rather than silently passing off mock numbers as
// real metrics.
const SampleDataChip: React.FC = () => (
  <Chip
    label="Sample data"
    size="small"
    variant="outlined"
    sx={{ ml: 1, fontSize: '0.7rem', height: 20, borderColor: 'warning.main', color: 'warning.dark' }}
  />
);

const Analytics: React.FC = () => {
  const [timeRange, setTimeRange] = useState<string>('30');
  const [selectedTab, setSelectedTab] = useState(0);
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [timeEntriesLoading, setTimeEntriesLoading] = useState(true);

  React.useEffect(() => {
    const fetchSummary = async () => {
      try {
        const data = await dashboardApi.getUserDashboardSummary();
        setSummary(data);
      } catch (error) {
        console.error('Failed to fetch dashboard summary', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSummary();
  }, []);

  React.useEffect(() => {
    const fetchTimeEntries = async () => {
      try {
        const entries = await timeTrackingApi.getUserEntries();
        setTimeEntries(entries);
      } catch (error) {
        console.error('Failed to fetch time entries', error);
      } finally {
        setTimeEntriesLoading(false);
      }
    };
    fetchTimeEntries();
  }, []);

  // Real time-tracking aggregates, derived client-side from the user's
  // actual logged entries - `duration` is in seconds.
  const timeStats = React.useMemo(() => {
    const totalSeconds = timeEntries.reduce((sum, e) => sum + (e.duration || 0), 0);
    const totalHours = totalSeconds / 3600;
    const daysWithEntries = new Set(
      timeEntries.map((e) => new Date(e.startTime).toDateString())
    ).size;
    const averageHoursPerDay = daysWithEntries > 0 ? totalHours / daysWithEntries : 0;

    const now = new Date();
    const weekly = Array.from({ length: 7 }).map((_, i) => {
      const day = new Date(now);
      day.setDate(now.getDate() - (6 - i));
      day.setHours(0, 0, 0, 0);
      const nextDay = new Date(day);
      nextDay.setDate(day.getDate() + 1);
      const hours =
        timeEntries
          .filter((e) => {
            const t = new Date(e.startTime).getTime();
            return t >= day.getTime() && t < nextDay.getTime();
          })
          .reduce((sum, e) => sum + (e.duration || 0), 0) / 3600;
      return { day: DAY_NAMES[day.getDay()], hours: Math.round(hours * 10) / 10 };
    });

    return { totalHours, averageHoursPerDay, weekly, entryCount: timeEntries.length };
  }, [timeEntries]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  // Render metric cards
  const renderMetricCard = (
    icon: React.ReactElement,
    title: string,
    value: string | number,
    subtitle?: string,
    trend?: { value: number; positive: boolean }
  ) => (
    <Card className="metric-card">
      <CardContent>
        <Box className="metric-card__header">
          <Box className="metric-card__icon">{icon}</Box>
          <Typography variant="caption" color="text.secondary">
            {title}
          </Typography>
        </Box>
        <Typography variant="h4" className="metric-card__value">
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
        {trend && (
          <Chip
            label={`${trend.positive ? '+' : ''}${trend.value}%`}
            size="small"
            color={trend.positive ? 'success' : 'error'}
            sx={{ mt: 1 }}
          />
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box className="analytics-page">
      {/* Header */}
      <Box className="analytics-page__header">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TrendingUp sx={{ fontSize: 48, color: '#4A90E2' }} />
          <Box>
            <Typography variant="h4" className="analytics-page__title">
              Analytics Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Comprehensive analytics and reporting
            </Typography>
          </Box>
        </Box>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Time Range</InputLabel>
          <Select
            value={timeRange}
            label="Time Range"
            onChange={(e) => setTimeRange(e.target.value)}
          >
            <MenuItem value="7">Last 7 days</MenuItem>
            <MenuItem value="30">Last 30 days</MenuItem>
            <MenuItem value="90">Last 90 days</MenuItem>
            <MenuItem value="365">Last year</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Key Metrics Overview */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <LinearProgress sx={{ width: '50%' }} />
        </Box>
      ) : summary ? (
        <Box
          className="analytics-page__metrics"
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' },
            gap: 3,
          }}
        >
          {renderMetricCard(
            <CheckCircle sx={{ color: '#50C878' }} />,
            'Tasks Completed',
            summary.completedTasks,
            `${summary.totalTasks > 0 ? Math.round((summary.completedTasks / summary.totalTasks) * 100) : 0}% completion rate`,
          )}
          {renderMetricCard(
            <Speed sx={{ color: '#4A90E2' }} />,
            'Active Sprints',
            summary.activeSprints,
            'Currently active across boards'
          )}
          {renderMetricCard(
            <Schedule sx={{ color: '#EF5350' }} />,
            'Overdue Tasks',
            summary.overdueTasks,
            'Tasks past due date'
          )}
          {renderMetricCard(
            <People sx={{ color: '#AB47BC' }} />,
            'Team Members',
            summary.workspaceStats?.totalMembers || 0,
            'Active contributors'
          )}
        </Box>
      ) : (
        <Typography color="error">Failed to load metrics.</Typography>
      )}
      {/* Tabs for different analytics sections */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={selectedTab} onChange={handleTabChange}>
          <Tab label="Overview" />
          <Tab label="Sprint Analytics" />
          <Tab label="Time Tracking" />
          <Tab label="Team Performance" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {selectedTab === 0 && (
        <Box className="analytics-page__content">
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 3 }}>
            {/* Task Completion Rates */}
            <Box sx={{ gridColumn: { xs: '1', lg: '1 / span 2' } }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    Task Completion Rates
                    <SampleDataChip />
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={taskCompletionData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Area
                        type="monotone"
                        dataKey="completed"
                        stackId="1"
                        stroke="#50C878"
                        fill="#50C878"
                        name="Completed"
                      />
                      <Area
                        type="monotone"
                        dataKey="inProgress"
                        stackId="1"
                        stroke="#FFB74D"
                        fill="#FFB74D"
                        name="In Progress"
                      />
                      <Area
                        type="monotone"
                        dataKey="created"
                        stackId="1"
                        stroke="#4A90E2"
                        fill="#4A90E2"
                        name="Created"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Box>

            {/* Productivity Insights */}
            <Box>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <EmojiEvents sx={{ color: '#FFB74D' }} />
                    <Typography variant="h6">Productivity Insights</Typography>
                    <SampleDataChip />
                  </Box>
                  <Box className="insights-list">
                    <Box className="insight-item">
                      <Typography variant="caption" color="text.secondary">
                        Peak Hours
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {productivityInsights.peakProductivityHours}
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 1.5 }} />
                    <Box className="insight-item">
                      <Typography variant="caption" color="text.secondary">
                        Most Productive Day
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {productivityInsights.mostProductiveDay}
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 1.5 }} />
                    <Box className="insight-item">
                      <Typography variant="caption" color="text.secondary">
                        Focus Time
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={productivityInsights.focusTimePercentage}
                          sx={{ flex: 1, height: 8, borderRadius: 4 }}
                        />
                        <Typography variant="body2" fontWeight={600}>
                          {productivityInsights.focusTimePercentage}%
                        </Typography>
                      </Box>
                    </Box>
                    <Divider sx={{ my: 1.5 }} />
                    <Box className="insight-item">
                      <Typography variant="caption" color="text.secondary">
                        Blocker Resolution Time
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {productivityInsights.blockerResolutionTime}h avg
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>

            {/* Burndown Chart */}
            <Box>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    Burndown Charts
                    <SampleDataChip />
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={burndownData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="day" label={{ value: 'Sprint Day', position: 'insideBottom', offset: -5 }} />
                      <YAxis label={{ value: 'Story Points', angle: -90, position: 'insideLeft' }} />
                      <Tooltip />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="ideal"
                        stroke="#BDBDBD"
                        strokeDasharray="5 5"
                        name="Ideal"
                      />
                      <Line
                        type="monotone"
                        dataKey="actual"
                        stroke="#4A90E2"
                        strokeWidth={2}
                        name="Actual"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Box>

            {/* Time logged this week - real, from the Time Tracking tab's data */}
            <Box>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Time Logged (Last 7 Days)
                  </Typography>
                  {timeEntriesLoading ? (
                    <LinearProgress sx={{ my: 4 }} />
                  ) : timeStats.weekly.every((d) => d.hours === 0) ? (
                    <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
                      No time logged in the last 7 days
                    </Box>
                  ) : (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={timeStats.weekly}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="day" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="hours" fill="#0073ea" name="Hours Logged" radius={[6, 6, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </Box>
          </Box>
        </Box>
      )}

      {selectedTab === 1 && (
        <Box className="analytics-page__content">
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Sprint Velocity Trends */}
            <Box>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <ShowChart sx={{ color: '#4A90E2' }} />
                    <Typography variant="h6">Sprint Velocity Trends</Typography>
                    <SampleDataChip />
                  </Box>
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={sprintVelocityData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="sprintName" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="plannedPoints" fill="#4A90E2" name="Planned Points" />
                      <Bar dataKey="completedPoints" fill="#50C878" name="Completed Points" />
                      <Bar dataKey="carryOverPoints" fill="#EF5350" name="Carry Over" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </Box>
      )}

      {selectedTab === 2 && (
        <Box className="analytics-page__content">
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 3 }}>
            <Card>
              <CardContent>
                <Typography variant="caption" color="text.secondary">
                  Total Hours Logged
                </Typography>
                {timeEntriesLoading ? (
                  <LinearProgress sx={{ my: 2 }} />
                ) : (
                  <>
                    <Typography variant="h4" sx={{ my: 1 }}>
                      {timeStats.totalHours.toFixed(1)}h
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {timeStats.averageHoursPerDay.toFixed(1)}h per active day average
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <Typography variant="caption" color="text.secondary">
                  Time Entries Logged
                </Typography>
                {timeEntriesLoading ? (
                  <LinearProgress sx={{ my: 2 }} />
                ) : (
                  <>
                    <Typography variant="h4" sx={{ my: 1 }}>
                      {timeStats.entryCount}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Across all your tasks
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <Typography variant="caption" color="text.secondary">
                  This Week
                </Typography>
                {timeEntriesLoading ? (
                  <LinearProgress sx={{ my: 2 }} />
                ) : (
                  <>
                    <Typography variant="h4" sx={{ my: 1 }}>
                      {timeStats.weekly.reduce((sum, d) => sum + d.hours, 0).toFixed(1)}h
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Last 7 days
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Weekly Time Trend - real, from the user's own time entries */}
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Weekly Time Tracking Trend
                  </Typography>
                  {timeEntriesLoading ? (
                    <LinearProgress sx={{ my: 4 }} />
                  ) : timeStats.weekly.every((d) => d.hours === 0) ? (
                    <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
                      No time logged in the last 7 days
                    </Box>
                  ) : (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={timeStats.weekly}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="day" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="hours" fill="#0073ea" name="Hours Logged" radius={[6, 6, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </Box>
          </Box>
        </Box>
      )}

      {selectedTab === 3 && (
        <Box className="analytics-page__content">
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Team Member Performance Table */}
            <Box>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <AnalyticsIcon sx={{ color: '#4A90E2' }} />
                    <Typography variant="h6">Team Performance</Typography>
                  </Box>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Team Member</TableCell>
                          <TableCell align="right">Tasks Completed</TableCell>
                          <TableCell align="right">Hours Logged</TableCell>
                          <TableCell align="right">Velocity</TableCell>
                          <TableCell align="right">Efficiency</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(summary?.topContributors || []).map((member) => (
                          <TableRow key={member.userId}>
                            <TableCell component="th" scope="row">
                              <Typography fontWeight={600}>{member.userName || member.userEmail}</Typography>
                            </TableCell>
                            <TableCell align="right">{member.completedTasks}</TableCell>
                            <TableCell align="right">N/A</TableCell>
                            <TableCell align="right">N/A</TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" color="text.secondary">N/A</Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default Analytics;
